import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Modal,
  Button,
  Upload,
  message,
  Alert,
  List,
  Typography,
  Steps,
  Progress,
  Spin,
  Select,
  Table,
  Space,
  Tag,
  Tooltip,
} from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  downloadLineItemTemplate,
  parseLineItemFile,
  validateAllRows,
  transformToLineItems,
  LINE_ITEM_FIELDS,
} from '../utils/lineItemImport.utils';
import { useAIColumnMatching } from '../hooks/useAIColumnMatching';

const { Text, Title, Paragraph } = Typography;
const { Step } = Steps;

// Limits for file validation
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_ROWS = 100;

/**
 * LineItemImportModal Component
 *
 * Multi-step modal for importing line items from CSV/Excel files.
 * Step 0: Upload file + download template
 * Step 1: Column mapping with manual override dropdowns
 * Step 2: Preview table with validation errors/warnings
 * Step 3: Import complete
 */
const LineItemImportModal = ({
  visible,
  onClose,
  onImport,
  campaignIndex,
  campaignDefaults = {},
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState({ rows: [], headers: [], columnMappings: {} });
  const [columnMappings, setColumnMappings] = useState({});
  const [validationResult, setValidationResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });

  // Track mounted state to prevent memory leaks from setState on unmounted component
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const { getFieldOptions } = useAIColumnMatching();
  const fieldOptions = useMemo(() => getFieldOptions(), [getFieldOptions]);

  const handleDownloadTemplate = () => {
    downloadLineItemTemplate();
    message.success('Template downloaded successfully');
  };

  const handleFileUpload = async (uploadedFile) => {
    if (!uploadedFile.name.endsWith('.csv') && !uploadedFile.name.endsWith('.xlsx') && !uploadedFile.name.endsWith('.xls')) {
      message.error('Please upload a CSV or Excel file');
      return false;
    }

    // Validate file size
    if (uploadedFile.size > MAX_FILE_SIZE) {
      message.error(`File too large. Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
      return false;
    }

    try {
      message.loading('Parsing file...', 0);
      const { rows, headers, columnMappings: autoMappings } = await parseLineItemFile(uploadedFile);
      message.destroy();

      // Check if component is still mounted
      if (!isMountedRef.current) return false;

      if (rows.length === 0) {
        message.error('File is empty or contains no valid data rows');
        return false;
      }

      // Validate row count
      if (rows.length > MAX_ROWS) {
        message.error(`Too many rows. Maximum is ${MAX_ROWS} line items per import. Found ${rows.length} rows.`);
        return false;
      }

      setFile(uploadedFile);
      setParsedData({ rows, headers, columnMappings: autoMappings });
      setColumnMappings(autoMappings);

      // Count how many columns were auto-mapped
      const mappedCount = Object.values(autoMappings).filter(m => m.field).length;
      message.success(`Parsed ${rows.length} row(s). Auto-matched ${mappedCount} of ${headers.length} columns.`);

      setCurrentStep(1);
    } catch (error) {
      message.destroy();
      if (isMountedRef.current) {
        message.error(error.message || 'Failed to parse file');
      }
    }

    return false;
  };

  const handleColumnMappingChange = (header, fieldKey) => {
    setColumnMappings(prev => ({
      ...prev,
      [header]: { ...prev[header], field: fieldKey },
    }));
  };

  const handleProceedToPreview = () => {
    // Check for duplicate column mappings
    const mappedFields = Object.values(columnMappings)
      .map(m => m?.field)
      .filter(Boolean);
    const duplicates = mappedFields.filter((field, index) => mappedFields.indexOf(field) !== index);

    if (duplicates.length > 0) {
      const duplicateLabels = [...new Set(duplicates)]
        .map(field => LINE_ITEM_FIELDS[field]?.label || field)
        .join(', ');
      message.error(`Duplicate column mappings detected: ${duplicateLabels}. Each field can only be mapped once.`);
      return;
    }

    const result = validateAllRows(parsedData.rows, columnMappings);
    setValidationResult(result);
    setCurrentStep(2);
  };

  const handleImport = async () => {
    if (!validationResult?.isValid) {
      message.error('Please fix validation errors before importing');
      return;
    }

    setImporting(true);
    setImportProgress({ current: 0, total: parsedData.rows.length });
    setCurrentStep(3);

    try {
      const lineItems = transformToLineItems(
        parsedData.rows,
        columnMappings,
        campaignDefaults
      );

      // Progress simulation with mounted check
      for (let i = 0; i < lineItems.length; i++) {
        if (!isMountedRef.current) return;
        setImportProgress({ current: i + 1, total: lineItems.length });
        await new Promise(resolve => setTimeout(resolve, 30));
      }

      if (!isMountedRef.current) return;

      onImport(lineItems, campaignIndex);
      message.success(`Successfully imported ${lineItems.length} line item(s)`);

      setTimeout(() => {
        if (isMountedRef.current) {
          handleClose();
        }
      }, 500);
    } catch (error) {
      if (isMountedRef.current) {
        message.error(`Import failed: ${error.message}`);
      }
    } finally {
      if (isMountedRef.current) {
        setImporting(false);
      }
    }
  };

  const handleReset = () => {
    setCurrentStep(0);
    setFile(null);
    setParsedData({ rows: [], headers: [], columnMappings: {} });
    setColumnMappings({});
    setValidationResult(null);
    setImportProgress({ current: 0, total: 0 });
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const getConfidenceBadge = (confidence) => {
    if (!confidence) return <Tag color="red">Not Mapped</Tag>;
    if (confidence >= 0.9) return <Tag color="green">Matched</Tag>;
    if (confidence >= 0.7) return <Tag color="gold">Likely</Tag>;
    return <Tag color="orange">Partial</Tag>;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div>
            <div style={{ display: 'flex', gap: 24, marginBottom: 24 }}>
              <div style={{
                flex: 1,
                padding: 24,
                border: '1px solid #d9d9d9',
                borderRadius: 8,
                backgroundColor: '#fafafa',
              }}>
                <Title level={5}>1. Download Template</Title>
                <Paragraph type="secondary">
                  Excel template matching Ad Ops format
                </Paragraph>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadTemplate}
                  size="large"
                  block
                >
                  Download Template
                </Button>
              </div>

              <div style={{
                flex: 1,
                padding: 24,
                border: file ? '1px solid #52c41a' : '1px solid #d9d9d9',
                borderRadius: 8,
                backgroundColor: file ? '#f6ffed' : '#fafafa',
              }}>
                <Title level={5}>2. Upload File</Title>
                <Paragraph type="secondary">
                  CSV or Excel file with line items
                </Paragraph>
                <Upload
                  accept=".csv,.xlsx,.xls"
                  beforeUpload={handleFileUpload}
                  showUploadList={false}
                  maxCount={1}
                  disabled={file}
                >
                  <Button
                    icon={<UploadOutlined />}
                    size="large"
                    block
                    type={file ? 'default' : 'primary'}
                    disabled={file}
                  >
                    Upload File
                  </Button>
                </Upload>
                {file && (
                  <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Text type="success">
                      <CheckCircleOutlined /> {file.name}
                    </Text>
                    <Button
                      type="link"
                      size="small"
                      danger
                      onClick={handleReset}
                      style={{ padding: 0 }}
                    >
                      Clear
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <Alert
              message="Supported Columns (Auto-Detected)"
              description={
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  <Tag>Line Item Name</Tag>
                  <Tag>Start Date</Tag>
                  <Tag>End Date</Tag>
                  <Tag>DSP Spend</Tag>
                  <Tag>Allocation%</Tag>
                  <Tag>Bid</Tag>
                  <Tag>Segments</Tag>
                  <Tag>Segment ID</Tag>
                  <Tag>Deal Name</Tag>
                  <Tag>ID (Deal)</Tag>
                </div>
              }
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
            />
          </div>
        );

      case 1:
        return (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">
                Review column mappings. Adjust any incorrect matches using the dropdowns.
              </Text>
            </div>

            <div style={{
              border: '1px solid #d9d9d9',
              borderRadius: 8,
              maxHeight: 400,
              overflow: 'auto',
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#fafafa' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #d9d9d9' }}>
                      CSV Column
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '1px solid #d9d9d9', width: 60 }}>
                      →
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #d9d9d9' }}>
                      Line Item Field
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '1px solid #d9d9d9', width: 100 }}>
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {parsedData.headers.map((header) => {
                    const mapping = columnMappings[header];
                    const currentField = mapping?.field;

                    return (
                      <tr key={header} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px 16px' }}>
                          <Text strong>"{header}"</Text>
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          →
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <Select
                            value={currentField}
                            onChange={(value) => handleColumnMappingChange(header, value)}
                            style={{ width: '100%' }}
                            options={fieldOptions}
                            placeholder="Select field..."
                          />
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          {getConfidenceBadge(mapping?.confidence)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 16 }}>
              <Text type="secondary">
                <InfoCircleOutlined /> Columns mapped to "-- Skip --" will be ignored.
              </Text>
            </div>
          </div>
        );

      case 2:
        return (
          <div>
            <Title level={5} style={{ marginBottom: 16 }}>
              Preview: {parsedData.rows.length} Line Item(s)
            </Title>

            {validationResult && !validationResult.isValid && (
              <Alert
                message="Validation Errors"
                description={`Found ${validationResult.errors.length} error(s) that must be fixed.`}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {validationResult?.errors?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <List
                  size="small"
                  bordered
                  dataSource={validationResult.errors}
                  renderItem={(error) => (
                    <List.Item>
                      <Text type="danger"><CloseCircleOutlined /> {error}</Text>
                    </List.Item>
                  )}
                  style={{ maxHeight: 120, overflow: 'auto' }}
                />
              </div>
            )}

            {validationResult?.warnings?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <List
                  size="small"
                  bordered
                  dataSource={validationResult.warnings}
                  renderItem={(warning) => (
                    <List.Item>
                      <Text type="warning"><WarningOutlined /> {warning}</Text>
                    </List.Item>
                  )}
                  style={{ maxHeight: 80, overflow: 'auto' }}
                />
              </div>
            )}

            <div style={{
              border: '1px solid #d9d9d9',
              borderRadius: 8,
              overflow: 'auto',
              maxHeight: 250,
            }}>
              <Table
                dataSource={parsedData.rows.map((row, index) => ({
                  key: index,
                  lineNumber: row.lineNumber,
                  ...row.data,
                }))}
                columns={[
                  {
                    title: '#',
                    dataIndex: 'lineNumber',
                    key: 'lineNumber',
                    width: 50,
                    fixed: 'left',
                  },
                  ...parsedData.headers
                    .filter(header => columnMappings[header]?.field)
                    .slice(0, 6) // Limit displayed columns for readability
                    .map(header => ({
                      title: (
                        <Tooltip title={`CSV: "${header}"`}>
                          <span>{LINE_ITEM_FIELDS[columnMappings[header]?.field]?.label || header}</span>
                        </Tooltip>
                      ),
                      dataIndex: header,
                      key: header,
                      ellipsis: true,
                      width: 150,
                    })),
                ]}
                pagination={false}
                size="small"
                scroll={{ x: 'max-content' }}
              />
            </div>

            {validationResult?.isValid && (
              <Alert
                message="Ready to Import"
                description={`${parsedData.rows.length} line item(s) will be added to the campaign.`}
                type="success"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}
          </div>
        );

      case 3:
        return (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            {importing ? (
              <>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
                <Title level={4} style={{ marginTop: 24 }}>Importing Line Items...</Title>
                <Progress
                  percent={importProgress.total > 0 ? Math.round((importProgress.current / importProgress.total) * 100) : 0}
                  status="active"
                  style={{ maxWidth: 400, margin: '16px auto' }}
                />
                <Text type="secondary">
                  Processing {importProgress.current} of {importProgress.total}
                </Text>
              </>
            ) : (
              <>
                <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a' }} />
                <Title level={4} style={{ marginTop: 24 }}>Import Complete!</Title>
                <Paragraph>Successfully imported {importProgress.total} line item(s).</Paragraph>
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const getFooterButtons = () => {
    if (importing) return null;

    const buttons = [];

    buttons.push(
      <Button key="cancel" onClick={handleClose}>
        {currentStep === 3 ? 'Close' : 'Cancel'}
      </Button>
    );

    switch (currentStep) {
      case 0:
        if (file && parsedData.headers.length > 0) {
          buttons.push(
            <Button
              key="next"
              type="primary"
              onClick={() => setCurrentStep(1)}
            >
              Next: Map Columns
            </Button>
          );
        }
        break;

      case 1:
        buttons.push(
          <Button key="back" onClick={() => setCurrentStep(0)}>
            Back
          </Button>
        );
        buttons.push(
          <Button
            key="preview"
            type="primary"
            onClick={handleProceedToPreview}
            disabled={!Object.values(columnMappings).some(v => v?.field)}
          >
            Next: Preview
          </Button>
        );
        break;

      case 2:
        buttons.push(
          <Button key="back" onClick={() => setCurrentStep(1)}>
            Back
          </Button>
        );
        buttons.push(
          <Button
            key="import"
            type="primary"
            onClick={handleImport}
            disabled={!validationResult?.isValid}
          >
            Import {parsedData.rows.length} Line Item(s)
          </Button>
        );
        break;

      case 3:
        if (!importing) {
          buttons.push(
            <Button key="importMore" type="primary" onClick={handleReset}>
              Import More
            </Button>
          );
        }
        break;

      default:
        break;
    }

    return buttons;
  };

  return (
    <Modal
      title={
        <span>
          <FileTextOutlined style={{ marginRight: 8 }} />
          Import Line Items
        </span>
      }
      width={800}
      open={visible}
      onCancel={importing ? null : handleClose}
      closable={!importing}
      maskClosable={!importing}
      footer={getFooterButtons()}
    >
      <Steps current={currentStep} size="small" style={{ marginBottom: 24 }}>
        <Step title="Upload" />
        <Step title="Map Columns" />
        <Step title="Preview" />
        <Step title="Import" />
      </Steps>

      {renderStepContent()}
    </Modal>
  );
};

export default LineItemImportModal;
